import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import FixVarianceModal from "@/components/FixVarianceModal";
import ExplainMovement from "@/components/ExplainMovement";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowsClockwise, Buildings, CheckCircle, WarningCircle, CaretRight, Wrench, Scales, Target, ShieldCheck, ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { useReturnToClose } from "@/hooks/useReturnToClose";

const SLUG_LEDGER = { prepayment: "/prepayments", "deferred-income": "/deferred-income", accrual: "/accruals", depreciation: "/fixed-assets" };

const META = {
  prepayment: { title: "Prepayments", code: "PREPAYMENT",
    subtitle: "How closely the expected prepayment balance (per the register) matches the actual Xero ledger balance, by nominal code." },
  "deferred-income": { title: "Deferred Income", code: "DEFERRED_INCOME",
    subtitle: "How closely the expected deferred-income balance (per the register) matches the actual Xero ledger balance, by nominal code." },
  accrual: { title: "Accruals", code: "ACCRUAL",
    subtitle: "How closely the expected accrual balance (per the register) matches the actual Xero ledger balance, by nominal code." },
  depreciation: { title: "Depreciation", code: "DEPRECIATION",
    subtitle: "How closely the expected accumulated-depreciation balance (per the register) matches the actual Xero ledger balance, by nominal code." },
};

const pctColor = (p) => (Math.abs(p) <= 1 ? "text-positive" : Math.abs(p) <= 5 ? "text-warn" : "text-negative");

function fmtAsAt(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function BalanceVariancePage({ category, embedded = false }) {
  const meta = META[category];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [fixRow, setFixRow] = useState(null);
  const [acceptRow, setAcceptRow] = useState(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/variance/${category}${refresh ? "?refresh=true" : ""}`);
      setData(data);
    } finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);
  useReturnToClose(!!data?.summary && data.summary.in_variance === 0, !loading && !!data);

  const reopen = async (r) => {
    try {
      await api.post("/variance/unaccept", { tenant_id: r.tenant_id, code: r.code, category: meta.code });
      toast.success(`Re-opened ${r.code} for action`);
      load();
      window.dispatchEvent(new Event("badges:refresh"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not re-open");
    }
  };

  const sum = data?.summary;
  const rows = data?.rows || [];
  const tenants = [...new Set(rows.map((r) => r.tenant_name))];

  const refreshBtn = (
    <button onClick={() => load(true)} disabled={loading} data-testid="variance-refresh-btn"
      className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
      <ArrowsClockwise size={16} /> Refresh
    </button>
  );

  const content = (
    <>
      {sum?.as_at && !loading && (
        <div data-testid="variance-as-at" className="-mt-2 mb-6 text-sm text-navy/70">
          Balances as at <span className="font-medium text-navy/75">{fmtAsAt(sum.as_at)}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <Stat label="Accuracy" value={sum?.accuracy != null ? `${sum.accuracy}%` : "—"} accent={sum?.accuracy != null ? (sum.accuracy >= 99 ? "positive" : sum.accuracy >= 95 ? "warn" : "negative") : "navy"} icon={Target} id="variance-accuracy" />
        <Stat label="Codes Checked" value={sum?.codes ?? "—"} id="variance-total" />
        <Stat label="In Variance" value={sum?.in_variance ?? "—"} accent={sum?.in_variance ? "negative" : "positive"} icon={sum?.in_variance ? WarningCircle : CheckCircle} id="variance-count" />
        <Stat label="Total Variance" value={sum ? money(sum.total_variance) : "—"} accent={sum?.total_variance ? "negative" : "navy"} mono id="variance-total-amt" />
      </div>

      {sum && sum.in_variance === 0 && !loading && rows.length > 0 && (
        <div data-testid="variance-all-clear"
          className="flex items-center gap-3 bg-positive/10 border border-positive/30 text-positive rounded-lg px-5 py-3.5 mb-6">
          <CheckCircle size={22} weight="fill" />
          <span className="text-sm font-medium">Every {meta.title.toLowerCase()} balance ties back to the register. Nothing to adjust.</span>
        </div>
      )}

      {sum?.balance_source === "estimate" && !loading && (
        <div data-testid="variance-estimate-banner"
          className="flex items-start gap-3 bg-warn/10 border border-warn/40 text-navy rounded-lg px-5 py-3.5 mb-6">
          <WarningCircle size={22} weight="fill" className="text-warn shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold">Showing estimated balances, not Xero's Trial Balance.</span>{" "}
            Reconnect Xero with the reports permission for live Trial Balance figures.
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th /><Th>Code</Th><Th>Account</Th>
              <Th right>Xero Ledger</Th><Th right>Expected</Th><Th right>Variance</Th><Th right>Var %</Th><Th>Status</Th><Th right>Action</Th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tn) => (
              <TenantRows key={tn} tenant={tn} rows={rows.filter((r) => r.tenant_name === tn)} category={category}
                expanded={expanded} setExpanded={setExpanded} onFix={setFixRow}
                onAccept={setAcceptRow} onReopen={reopen} />
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="variance-empty">
            <div className="font-heading font-bold text-lg text-navy">No {meta.title.toLowerCase()} balances yet</div>
            <p className="text-sm mt-1">Create schedules on these nominal codes to track variances here.</p>
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-navy/65 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      <FixVarianceModal open={Boolean(fixRow)} row={fixRow} category={category}
        onClose={() => setFixRow(null)} onFixed={() => { load(true); window.dispatchEvent(new Event("badges:refresh")); }} />
      <AcceptVarianceModal open={Boolean(acceptRow)} row={acceptRow} category={meta.code}
        onClose={() => setAcceptRow(null)} onAccepted={() => { load(); window.dispatchEvent(new Event("badges:refresh")); }} />
    </>
  );

  if (embedded) {
    return (
      <div data-testid={`variance-embedded-${category}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="text-sm text-navy/70 max-w-2xl">{meta.subtitle}</p>
          {refreshBtn}
        </div>
        {content}
      </div>
    );
  }

  return (
    <Page>
      <PageHeader overline="Month-end" title={meta.title} subtitle={meta.subtitle}>
        {refreshBtn}
      </PageHeader>
      {content}
    </Page>
  );
}

function TenantRows({ tenant, rows, category, expanded, setExpanded, onFix, onAccept, onReopen }) {
  const navigate = useNavigate();
  const reviewInLedger = (r) => navigate(`${SLUG_LEDGER[category] || "/register"}?code=${encodeURIComponent(r.code)}`);
  return (
    <>
      <tr className="bg-navy/[0.03]">
        <td colSpan={9} className="px-5 py-2">
          <div className="flex items-center gap-2 overline text-navy/70"><Buildings size={14} /> {tenant}</div>
        </td>
      </tr>
      {rows.map((r) => {
        const key = `${r.tenant_id}-${r.code}`;
        const isOpen = expanded[key];
        const variance = r.status === "Variance";
        const accepted = r.status === "Accepted";
        const hasVar = variance || accepted;
        return (
          <Fragment key={key}>
            <tr key={key} data-testid={`variance-row-${key}`}
              className={`border-b border-slate-100 ${variance ? "bg-negative/[0.03]" : accepted ? "bg-amber-50/40" : ""}`}>
              <Td>
                {r.lines.length > 0 && (
                  <button onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                    data-testid={`variance-expand-${key}`}
                    className="text-navy/65 hover:text-navy transition-colors">
                    <CaretRight size={15} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  </button>
                )}
              </Td>
              <Td mono className="font-semibold">{r.code}</Td>
              <Td>{r.account_name}</Td>
              <Td right mono>{money(r.xero_balance, r.currency)}</Td>
              <Td right mono>{money(r.expected_balance, r.currency)}</Td>
              <Td right mono className={hasVar ? (variance ? "text-negative font-semibold" : "text-amber-700 font-semibold") : "text-navy/65"}>
                {money(r.variance, r.currency)}
              </Td>
              <Td right mono className={hasVar ? `${variance ? pctColor(r.variance_pct) : "text-amber-700"} font-semibold` : "text-navy/65"}>
                {r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%
              </Td>
              <Td>
                {variance && (
                  <span data-testid={`variance-status-${key}`} className="inline-flex items-center gap-1 text-xs font-semibold text-negative bg-negative/10 px-2 py-0.5 rounded-md">
                    <WarningCircle size={12} weight="fill" /> Variance
                  </span>
                )}
                {accepted && (
                  <span data-testid={`variance-status-${key}`} title={r.accepted_note ? `${r.accepted_note}${r.accepted_by ? ` — ${r.accepted_by}` : ""}` : r.accepted_by}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md">
                    <ShieldCheck size={12} weight="fill" /> Accepted
                  </span>
                )}
                {!hasVar && (
                  <span data-testid={`variance-status-${key}`} className="inline-flex items-center gap-1 text-xs font-semibold text-positive bg-positive/10 px-2 py-0.5 rounded-md">
                    <CheckCircle size={12} weight="fill" /> Reconciled
                  </span>
                )}
              </Td>
              <Td right>
                <div className="flex items-center justify-end gap-2">
                  {(variance || accepted) && (
                    <ExplainMovement
                      endpoint="/explain/variance"
                      body={{ tenant_id: r.tenant_id, code: r.code, category: META[category].code }}
                      title={`${r.code} — ${r.account_name}`}
                      subtitle={`${META[category].title} balance and variance, in plain English.`}
                      testid={`variance-explain-${key}`} />
                  )}
                  {(variance || accepted) && (
                    <button onClick={() => reviewInLedger(r)} data-testid={`variance-review-${key}`}
                      title={`Review ${r.code} in the ledger`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy/70 hover:border-orange hover:text-navy transition-colors">
                      <MagnifyingGlass size={13} /> Review in ledger
                    </button>
                  )}
                  {variance && (
                    <>
                      <button onClick={() => onFix(r)} data-testid={`variance-fix-${key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange/10 text-orangeink hover:bg-orange hover:text-white transition-colors">
                        <Wrench size={13} /> Adjust variance
                      </button>
                      <button onClick={() => onAccept(r)} data-testid={`variance-accept-${key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5 transition-colors">
                        Leave as is
                      </button>
                    </>
                  )}
                  {accepted && (
                    <button onClick={() => onReopen(r)} data-testid={`variance-reopen-${key}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5 transition-colors">
                      <ArrowCounterClockwise size={13} /> Re-open
                    </button>
                  )}
                </div>
              </Td>
            </tr>
            {isOpen && r.lines.map((ln, i) => (
              <tr key={`${key}-${i}`} className="border-b border-slate-50 bg-slate-50/40" data-testid={`variance-line-${key}-${i}`}>
                <Td />
                <Td colSpan={2}>
                  <div className="flex items-center gap-2 text-navy/70 pl-4">
                    <Scales size={13} className="text-navy/30" />
                    <span className="font-medium text-navy">{ln.contact}</span>
                    <span className="text-navy/65 text-xs">· {ln.doc_number}</span>
                    <span className="text-[0.6rem] uppercase tracking-wider text-navy/65">{ln.status}</span>
                  </div>
                </Td>
                <Td right mono className="text-navy/65">—</Td>
                <Td right mono className="text-navy/70">{money(ln.remaining_balance, r.currency)}</Td>
                <Td colSpan={4} />
              </tr>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

function Stat({ label, value, accent = "navy", mono, icon: Icon, id }) {
  const color = accent === "positive" ? "text-positive" : accent === "negative" ? "text-negative" : accent === "warn" ? "text-warn" : "text-navy";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5" data-testid={id}>
      <div className="flex items-center justify-between">
        <div className="overline text-navy/65">{label}</div>
        {Icon && <Icon size={18} className={color} />}
      </div>
      <div className={`${mono ? "font-mono text-2xl" : "font-heading font-black text-4xl tracking-tighter"} mt-2 tabular ${color}`}>
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, right }) => <th className={`px-5 py-3 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap text-[0.66rem] uppercase tracking-wider text-slate-600 bg-slate-50`}>{children}</th>;
const Td = ({ children, right, mono, className = "", colSpan }) =>
  <td colSpan={colSpan} className={`px-5 py-3 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;

function AcceptVarianceModal({ open, onClose, onAccepted, row, category }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setNote(""); }, [open]);
  if (!row) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/variance/accept", {
        tenant_id: row.tenant_id, code: row.code, category, variance: row.variance, note: note.trim(),
      });
      toast.success(`Variance on ${row.code} left as-is (accepted)`);
      onAccepted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept variance");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-lg border-slate-200 p-0 gap-0" data-testid="accept-variance-dialog">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-amber-600 mb-1">Leave as is</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">
            Accept {row.code} variance of {money(row.variance, row.currency)}
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            Marks this variance as reviewed and expected — no journal is posted. It stops being flagged
            until the variance changes. You can re-open it anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="px-7 py-6">
          <span className="overline text-navy/70 block mb-1.5">Reason (optional, recorded to audit)</span>
          <Textarea data-testid="accept-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            className="rounded-lg border-slate-200 focus:border-orange" placeholder="e.g. Immaterial rounding difference / known timing item" />
        </div>
        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100">
          <button onClick={onClose} data-testid="accept-cancel-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="accept-submit-btn"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">
            <ShieldCheck size={16} />{saving ? "SAVING…" : "Accept & leave as is"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
